import { QueryClient } from '@tanstack/react-query';

/**
 * TanStack Query Client Configuration
 *
 * Centralized QueryClient with sensible defaults for Molchanovs Move.
 *
 * Default Configuration:
 * - staleTime: 5 minutes - Data considered fresh, prevents unnecessary refetches
 * - gcTime: 10 minutes - Keep unused data in cache for back navigation
 * - retry: 3 - Retry failed requests with exponential backoff
 * - refetchOnWindowFocus: true - Keep data fresh when user returns
 *
 * @see https://tanstack.com/query/latest/docs/framework/react/guides/important-defaults
 * @added DOC-0032 (2025-12-15)
 */

const FIVE_MINUTES_MS = 5 * 60 * 1000;
const TEN_MINUTES_MS = 10 * 60 * 1000;
const MAX_RETRY_DELAY_MS = 30_000;

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Data considered fresh for 5 minutes - reduces unnecessary refetches
      staleTime: FIVE_MINUTES_MS,

      // Keep unused data in cache for 10 minutes - improves back navigation UX
      gcTime: TEN_MINUTES_MS,

      // Retry failed requests 3 times with exponential backoff
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, MAX_RETRY_DELAY_MS),

      // Refetch when window regains focus - keeps data fresh
      refetchOnWindowFocus: true,

      // Don't refetch on mount if data is fresh (within staleTime)
      refetchOnMount: false,

      // Don't refetch on reconnect by default (most data doesn't need this)
      refetchOnReconnect: false,
    },
    mutations: {
      // No automatic retry for mutations - user should retry explicitly
      retry: false,
    },
  },
});
